import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/design05/Header';
import {
    Coffee,
    Award,
    Heart,
    Shield,
    Star,
    ArrowLeft,
    CheckCircle,
    Thermometer,
    Zap,
    Globe,
    ArrowRight,
    Target,
} from 'lucide-react';

const CinnamonPage = () => {
    useEffect(() => {
        const setNamedMeta = (name, content) => {
            let tag = document.querySelector(`meta[name="${name}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute('name', name);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };

        const setPropertyMeta = (property, content) => {
            let tag = document.querySelector(`meta[property="${property}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute('property', property);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };

        const setCanonical = (href) => {
            let link = document.querySelector('link[rel="canonical"]');
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', 'canonical');
                document.head.appendChild(link);
            }
            link.setAttribute('href', href);
        };

        document.title = `Ceylon Cinnamon - The True Cinnamon | Claude's Special`;
        setNamedMeta(
            'description',
            `Discover authentic Ceylon Cinnamon from Sri Lanka. Learn about the world's finest cinnamon variety, its health benefits, and why it's considered the gold standard of spices.`
        );
        setNamedMeta(
            'keywords',
            'Ceylon Cinnamon, true cinnamon, Cinnamomum verum, Sri Lankan cinnamon, health benefits, premium spices'
        );
        setPropertyMeta('og:title', 'Ceylon Cinnamon - The True Cinnamon');
        setPropertyMeta(
            'og:description',
            "Authentic Ceylon Cinnamon from Sri Lanka - the world's finest cinnamon with unmatched quality and health benefits."
        );
        setPropertyMeta('og:type', 'article');
        setCanonical('/ceylon-cinnamon');
    }, []);

    return (
        <>
            <Header />

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black">
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-orange-500/20 via-red-500/10 to-yellow-500/20"></div>
                    <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-br from-orange-500/30 to-red-500/30 transform rotate-45 blur-3xl"></div>
                    <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-br from-yellow-500/30 to-orange-500/30 transform -rotate-45 blur-3xl"></div>
                </div>

                <div className="relative z-10 text-center max-w-7xl mx-auto px-6 pt-32">
                    <div className="mb-12">
                        <Link
                            to="/"
                            className="inline-flex items-center space-x-4 bg-white/10 backdrop-blur-xl border-2 border-white/30 text-white px-8 py-4 mb-8 hover:bg-white/20 transition-colors duration-200 font-bold tracking-wide"
                        >
                            <ArrowLeft className="h-6 w-6" />
                            <span>BACK TO HOME</span>
                        </Link>
                    </div>

                    <div className="inline-flex items-center justify-center mb-12">
                        <div className="relative">
                            <div className="w-32 h-32 bg-gradient-to-br from-orange-500 to-red-600 transform rotate-45 flex items-center justify-center shadow-2xl">
                                <Coffee className="h-16 w-16 text-white transform -rotate-45" />
                            </div>
                            <div className="absolute -top-4 -right-4 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                                <Star className="h-6 w-6 text-gray-900" />
                            </div>
                        </div>
                    </div>

                    <h1 className="text-8xl md:text-9xl font-black mb-8 leading-none tracking-tighter">
                        <span className="block text-white mb-4">CEYLON</span>
                        <span className="block bg-gradient-to-r from-orange-400 via-red-500 to-yellow-400 bg-clip-text text-transparent">
                            CINNAMON
                        </span>
                    </h1>

                    <div className="w-32 h-2 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-12"></div>

                    <p className="text-3xl md:text-4xl mb-8 font-bold text-gray-300 tracking-wide">
                        THE WORLD'S ONLY TRUE CINNAMON
                    </p>

                    <p className="text-xl mb-16 text-gray-400 max-w-4xl mx-auto leading-relaxed">
                        Scientifically known as Cinnamomum verum, Ceylon Cinnamon is nature's most precious spice, cultivated
                        exclusively in Sri Lanka's mystical highlands for over 2,000 years.
                    </p>
                </div>
            </section>

            {/* What Makes It Special */}
            <section className="py-32 bg-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
                    <div className="text-center mb-20">
                        <div className="inline-block bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 mb-8 font-black tracking-widest">
                            WHAT MAKES IT SPECIAL
                        </div>
                        <h2 className="text-6xl font-black text-gray-900 mb-8 tracking-tight">
                            THE CROWN JEWEL
                            <br />
                            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">OF SPICES</span>
                        </h2>
                        <div className="w-32 h-2 bg-gradient-to-r from-orange-500 to-red-600 mx-auto"></div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="group text-center bg-gray-100 shadow-2xl hover:shadow-orange-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-500 to-red-600"></div>
                            <div className="p-12">
                                <div className="relative mb-8">
                                    <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-red-600 transform rotate-45 flex items-center justify-center mx-auto group-hover:rotate-90 transition-all duration-500 shadow-xl">
                                        <Coffee className="h-12 w-12 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center">
                                        <Star className="h-4 w-4 text-white" />
                                    </div>
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">TRUE CINNAMON</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    The only authentic cinnamon species recognized by botanists worldwide, native exclusively to Sri Lanka.
                                </p>
                            </div>
                        </div>

                        <div className="group text-center bg-gray-100 shadow-2xl hover:shadow-green-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 to-emerald-600"></div>
                            <div className="p-12">
                                <div className="relative mb-8">
                                    <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 transform rotate-45 flex items-center justify-center mx-auto group-hover:rotate-90 transition-all duration-500 shadow-xl">
                                        <Heart className="h-12 w-12 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center">
                                        <CheckCircle className="h-4 w-4 text-white" />
                                    </div>
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">ULTRA-SAFE</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Contains less than 0.004% coumarin compared to cassia's 5%, making it completely safe for daily
                                    consumption.
                                </p>
                            </div>
                        </div>

                        <div className="group text-center bg-gray-100 shadow-2xl hover:shadow-purple-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-purple-500 to-pink-600"></div>
                            <div className="p-12">
                                <div className="relative mb-8">
                                    <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-600 transform rotate-45 flex items-center justify-center mx-auto group-hover:rotate-90 transition-all duration-500 shadow-xl">
                                        <Zap className="h-12 w-12 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                                        <Star className="h-4 w-4 text-white" />
                                    </div>
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">EXQUISITE FLAVOR</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Delicate, sweet, and complex flavor profile with subtle citrus notes that enhance culinary creations.
                                </p>
                            </div>
                        </div>

                        <div className="group text-center bg-gray-100 shadow-2xl hover:shadow-blue-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
                            <div className="p-12">
                                <div className="relative mb-8">
                                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 transform rotate-45 flex items-center justify-center mx-auto group-hover:rotate-90 transition-all duration-500 shadow-xl">
                                        <Award className="h-12 w-12 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-400 rounded-full flex items-center justify-center">
                                        <Globe className="h-4 w-4 text-white" />
                                    </div>
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">GLOBAL RECOGNITION</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Preferred by world-renowned chefs, health experts, and discerning consumers worldwide.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Health Benefits */}
            <section className="py-32 bg-gray-100">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <div className="inline-block bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 mb-8 font-black tracking-widest">
                            HEALTH &amp; WELLNESS
                        </div>
                        <h2 className="text-6xl font-black text-gray-900 mb-8 tracking-tight">
                            NATURE'S MOST POWERFUL
                            <br />
                            <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                                WELLNESS SPICE
                            </span>
                        </h2>
                        <div className="w-32 h-2 bg-gradient-to-r from-green-500 to-emerald-600 mx-auto"></div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="group bg-white shadow-2xl hover:shadow-red-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-500 to-pink-600"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-600 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <Heart className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">CARDIOVASCULAR SUPPORT</h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    Supports healthy cholesterol levels and promotes optimal blood circulation.
                                </p>
                                <div className="flex items-center text-red-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>CLINICALLY STUDIED</span>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-white shadow-2xl hover:shadow-blue-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <Thermometer className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">BLOOD SUGAR BALANCE</h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    May help support healthy blood sugar levels and improve insulin sensitivity.
                                </p>
                                <div className="flex items-center text-blue-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>RESEARCH BACKED</span>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-white shadow-2xl hover:shadow-green-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 to-emerald-600"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <Shield className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">ANTIOXIDANT POWERHOUSE</h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    Packed with powerful antioxidants that help protect cells from oxidative stress.
                                </p>
                                <div className="flex items-center text-green-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>HIGH ORAC VALUE</span>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-white shadow-2xl hover:shadow-yellow-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-yellow-500 to-orange-500"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-yellow-500 to-orange-500 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <Zap className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">ANTI-INFLAMMATORY</h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    Natural anti-inflammatory compounds help reduce inflammation markers.
                                </p>
                                <div className="flex items-center text-yellow-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>NATURAL COMPOUNDS</span>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-white shadow-2xl hover:shadow-purple-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-purple-500 to-indigo-600"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <Target className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">COGNITIVE SUPPORT</h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    May support brain health and cognitive function through neuroprotective compounds.
                                </p>
                                <div className="flex items-center text-purple-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>NEUROPROTECTIVE</span>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-white shadow-2xl hover:shadow-teal-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-teal-500 to-cyan-600"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-600 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <CheckCircle className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">SAFE DAILY USE</h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    Ultra-low coumarin content makes it completely safe for daily consumption.
                                </p>
                                <div className="flex items-center text-teal-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>FDA RECOGNIZED</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-orange-500/20 via-red-500/10 to-yellow-500/20"></div>
                    <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-orange-500/30 to-red-500/30 transform rotate-45 blur-3xl"></div>
                    <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-br from-yellow-500/30 to-orange-500/30 transform -rotate-45 blur-3xl"></div>
                </div>

                <div className="max-w-5xl mx-auto text-center px-6 lg:px-12 relative">
                    <div className="inline-block bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 mb-12 font-black tracking-widest">
                        READY TO EXPERIENCE?
                    </div>

                    <h2 className="text-6xl md:text-7xl font-black text-white mb-8 leading-tight tracking-tight">
                        EXPERIENCE THE
                        <br />
                        <span className="bg-gradient-to-r from-orange-400 via-red-500 to-yellow-400 bg-clip-text text-transparent">
                            TRUE DIFFERENCE
                        </span>
                    </h2>

                    <div className="w-32 h-2 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-12"></div>

                    <p className="text-xl text-gray-300 mb-16 leading-relaxed max-w-4xl mx-auto">
                        Join Australia's leading importers and health-conscious consumers who choose authentic Ceylon Cinnamon
                        for its unmatched quality, safety, and health benefits.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                        <Link
                            to="/#contact"
                            className="group bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-12 py-6 text-xl font-black tracking-wide transition-all duration-500 transform hover:scale-110 shadow-2xl hover:shadow-orange-500/50 flex items-center space-x-4"
                        >
                            <span>GET WHOLESALE PRICING</span>
                            <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" />
                        </Link>
                        <a
                            href="mailto:exports@claudesspecial.lk"
                            className="bg-transparent border-4 border-white hover:bg-white hover:text-gray-900 text-white px-12 py-6 text-xl font-black tracking-wide transition-all duration-500 transform hover:scale-110"
                        >
                            EMAIL DIRECT
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center">
                        <div className="flex items-center justify-center space-x-4 mb-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 transform rotate-45 flex items-center justify-center shadow-xl">
                                <Coffee className="h-8 w-8 text-white transform -rotate-45" />
                            </div>
                            <div>
                                <div className="text-3xl font-black text-white tracking-wide">CLAUDE'S SPECIAL</div>
                                <div className="text-orange-400 font-bold tracking-widest">AUTHENTIC CEYLON CINNAMON</div>
                            </div>
                        </div>
                        <div className="w-32 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-8"></div>
                        <p className="text-gray-300 mb-8 max-w-3xl mx-auto text-lg">
                            The world's finest Ceylon Cinnamon, sourced directly from Sri Lanka's pristine highlands and
                            delivered to Australia with uncompromising quality and authenticity.
                        </p>
                        <div className="flex justify-center space-x-12 text-gray-300 mb-8">
                            <span className="flex items-center space-x-3">
                                <Coffee className="h-6 w-6 text-orange-400" />
                                <span className="font-bold">PREMIUM QUALITY</span>
                            </span>
                            <span className="flex items-center space-x-3">
                                <Shield className="h-6 w-6 text-green-400" />
                                <span className="font-bold">SAFE DAILY USE</span>
                            </span>
                            <span className="flex items-center space-x-3">
                                <Globe className="h-6 w-6 text-blue-400" />
                                <span className="font-bold">GLOBAL EXPORT</span>
                            </span>
                        </div>
                        <p className="text-gray-500 font-bold tracking-wide">
                            &copy; 2025 CLAUDE'S SPECIAL. ALL RIGHTS RESERVED.
                        </p>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default CinnamonPage;
